import { useMutation } from '@apollo/client/react';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetAllCards } from '../../../hooks/apolloHooks/useGetAllCards';
import { useGetCardInvoicesPaidRemains } from '../../../hooks/apolloHooks/useGetCardInvoicesPaidRemains';
import { useGetCustomer } from '../../../hooks/apolloHooks/useGetCustomer';
import { useUpdateCustomerFrozen } from '../../../hooks/apolloHooks/useUpdateCustomerFrozen';
import { GET_BASIC_CUSTOMER_DATA_QUERY, GET_CONTACT_CUSTOMER_DATA_QUERY, GET_CUSTOMER_AGREEMENTS_QUERY, GET_TRANSACTIONS_FULL_CUSTOMER_DATA_QUERY } from '../../../integrations/allkort/graphql/gql/CustomerQueries';
import { ADD_CUSTOMER_TASK_MUTATION } from '../../../integrations/allkort/graphql/gql/CustomerTasksMutations';
import { BasicCustomerDataResponse, ContactDataResponse, CustomerAgreementsResponse, Transaction, TransactionsFullCustomerDataResponse } from '../../../integrations/allkort/graphql/interface/Customer';
import { AddCustomerTaskInput, AddCustomerTaskResponse } from '../../../integrations/allkort/graphql/interface/CustomerTaskMutation';
import { validateBankAccount, validateBankClearing, validateBankgiro } from '../../../utils/bankPaymentValidationSchemas';
import { EMAIL_TYPES } from '../../../utils/filterEmailsByType';
import { formatSEK } from '../../../utils/formatSEK';
import FetchingAnimation from '../../elements/FetchingAnimation';
import Link from '../../elements/Link';
import RightsWrapper from '../../elements/RightsWrapper';
import { ArrowRightIcon, BankIcon, CardsStackIcon, DonationIcon, LogoutIcon, MobileIcon, ReceiptIcon, ServicesIcon, SingleCardIcon, SupportIcon, WalletIcon, WarningCircleIcon } from '../../svgs/ExpressonSVGs';


type DeleteAccountPageProps = {}

const DeleteAccountPage: React.FC<DeleteAccountPageProps> = () => {

    const [remainingBalanceChoice, setRemainingBalanceChoice] = useState<'refund' | 'donate' | null>(null);
    const [confirmChecked, setConfirmChecked] = useState(false);

    const [bankClearing, setBankClearing] = useState('');
    const [bankAccount, setBankAccount] = useState('');
    const [bankGiro, setBankGiro] = useState('');
    const [bankClearingError, setBankClearingError] = useState<string | undefined>();
    const [bankAccountError, setBankAccountError] = useState<string | undefined>();
    const [bankGiroError, setBankGiroError] = useState<string | undefined>();
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const handleBankClearingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^\d-]/g, '');
        setBankClearing(value);
        if (value.length >= 4) {
            const { error } = validateBankClearing(value);
            setBankClearingError(error);
        } else {
            setBankClearingError(undefined);
        }
    };

    const handleBankClearingBlur = () => {
        if (bankClearing) {
            const { error } = validateBankClearing(bankClearing);
            setBankClearingError(error);
        }
    };

    const handleBankAccountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^\d]/g, '');
        setBankAccount(value);
        if (value.length >= 7) {
            const { error } = validateBankAccount(value);
            setBankAccountError(error);
        } else {
            setBankAccountError(undefined);
        }
    };

    const handleBankAccountBlur = () => {
        if (bankAccount) {
            const { error } = validateBankAccount(bankAccount);
            setBankAccountError(error);
        }
    };

    const handleBankGiroChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^\d-]/g, '');
        setBankGiro(value);
        if (value.replace(/-/g, '').length >= 2) {
            const { error } = validateBankgiro(value);
            setBankGiroError(error);
        } else {
            setBankGiroError(undefined);
        }
    };

    const handleBankGiroBlur = () => {
        if (bankGiro) {
            const { error } = validateBankgiro(bankGiro);
            setBankGiroError(error);
        }
    };

    // Fetch all paid/remains data with auto-pagination
    const { paidRemains, loading: paidRemainsLoading } = useGetCardInvoicesPaidRemains();

    // Helper to check if an invoice is unpaid
    // An invoice is unpaid if: remains > 0 OR remains is null (unknown/unpaid)
    const isInvoiceUnpaid = (inv: any) => {
        return inv.remains === null || (inv.remains && inv.remains > 0);
    };

    // Get unpaid invoices count
    const unpaidInvoices = paidRemains.filter(isInvoiceUnpaid);
    const unpaidInvoicesCount = unpaidInvoices.length;


    // Fetch ALL cards with automatic pagination (up to 200 cards)
    const { cards: allCards, loading: cardsLoading, error: cardsError } = useGetAllCards();

    // -------------------------------------------------------------------------
    // Fetch K-type (credit/tankkort) transactions for current month
    // Used to warn about pending invoices that haven't been generated yet
    // -------------------------------------------------------------------------
    const [kTransactions, setKTransactions] = useState<Transaction[]>([]);
    const [kTxBefore, setKTxBefore] = useState<string | null>(null);
    const [kTxLastCursor, setKTxLastCursor] = useState<string | null>(null);
    const [kTxLoadingDone, setKTxLoadingDone] = useState(false);

    const kTxDateRange = useMemo(() => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        startOfMonth.setHours(0, 0, 0, 0);
        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);
        return { start: startOfMonth, end: endOfToday };
    }, []);

    const kTxWhereClause = useMemo(() => {
        const tzOffset = kTxDateRange.start.getTimezoneOffset() * 60 * 1000;
        return {
            transactionType: { eq: 'K' },
            quantity: { gt: 0 },
            at: {
                gt: new Date(kTxDateRange.start.getTime() - tzOffset).toISOString(),
                lt: new Date(kTxDateRange.end.getTime() - tzOffset).toISOString(),
            },
        };
    }, [kTxDateRange]);

    const { data: kTxData, loading: kTxLoading } = useGetCustomer<TransactionsFullCustomerDataResponse>(
        GET_TRANSACTIONS_FULL_CUSTOMER_DATA_QUERY,
        { before: kTxBefore, last: 10, where: kTxWhereClause },
        { fetchPolicy: 'cache-first' }
    );

    useEffect(() => {
        if (kTxLoading || !kTxData?.currentCustomer?.transactions) return;
        const { pageInfo, nodes } = kTxData.currentCustomer.transactions;
        if (!nodes || nodes.length === 0) { setKTxLoadingDone(true); return; }
        const currentCursor = pageInfo?.startCursor;
        if (kTxLastCursor === currentCursor) return;
        setKTransactions(prev => {
            const seen = new Set(prev.map(t => `${t.customerId}-${t.at}`));
            return [...prev, ...nodes.filter(t => !seen.has(`${t.customerId}-${t.at}`))];
        });
        setKTxLastCursor(currentCursor);
        if (pageInfo?.hasPreviousPage && pageInfo?.startCursor) {
            setKTxBefore(pageInfo.startCursor);
        } else {
            setKTxLoadingDone(true);
        }
    }, [kTxData, kTxLoading, kTxLastCursor]);

    // Derived: does this customer have K-type transactions this month?
    const hasKTransactionsThisMonth = kTxLoadingDone && kTransactions.length > 0;

    // Non-company: warn if today is between 1st-15th (invoice generates on the 16th)
    const todayDayOfMonth = new Date().getDate();
    const isInPreInvoicePeriod = todayDayOfMonth >= 1 && todayDayOfMonth <= 15;

    // Filter cards by type (P = prepaid/smartcards, K = klubkort/tankkort/creditcards)
    // Only count cards that are NOT disabled
    const hasActiveSmartCard = allCards.some((card: any) => card.type?.shortName === 'P' && card.limit?.disabled?.isDisabled !== true);
    const hasActiveCreditCard = allCards.some((card: any) => card.type?.shortName === 'K' && card.limit?.disabled?.isDisabled !== true);
    const smartCardCount = allCards.filter((card: any) => card.type?.shortName === 'P' && card.limit?.disabled?.isDisabled !== true).length;
    const creditCardCount = allCards.filter((card: any) => card.type?.shortName === 'K' && card.limit?.disabled?.isDisabled !== true).length;

    // Fetch customer data to check company,balance etc.
    const { data: customerBasicData, loading: customerLoading, error: customerError } = useGetCustomer<BasicCustomerDataResponse>(
        GET_BASIC_CUSTOMER_DATA_QUERY,
        undefined
    );

    if (customerError) console.error('Failed to load customer data:', customerError.message)

    const isCompany = customerBasicData?.currentCustomer?.isCompany ?? false;
    const remainingBalance = customerBasicData?.currentCustomer?.balance?.remainingBalance ?? 0;

    const BATCH_SIZE = 15; // Number of agreements to fetch per request
    const { data: agreementsData, loading: agreementsLoading, error: agreementsError } = useGetCustomer<CustomerAgreementsResponse>(
        GET_CUSTOMER_AGREEMENTS_QUERY,
        { first: BATCH_SIZE, after: null, before: null, last: null }
    );

    // Console log errors
    if (agreementsError) console.error('Failed to load agreements:', agreementsError.message)

    /**
     * Check if customer has approved the Q-Star App Terms & Conditions agreement
     * 
     * AGREEMENT IDs (approval.agreement.id values):
     *   7   = Reklam - Allow marketing communications
     *   8   = Tredjepartsreklam - Allow third-party marketing communications
     *   332 = Autogiro - Allow automatic payment (Autogiro)
     *   347 = Allmänna villkor - Informed of general terms for customer card
     *   350 = Kundklubb - Customer Club general terms
     *   354 = Användarvillkor Qstar App - Q-Star App Terms & Conditions
     * 
     * Note: We check agreement.id (not agreement.type.id) because agreement.id is unique per agreement
     */
    const hasAppAgreement = agreementsData?.currentCustomer?.agreementApprovals?.nodes?.some(
        approval => approval.agreement.id === 354
    ) ?? false;

    const { data: customerContactData, loading: customerContactLoading, error: customerContactError, refetch: refetchContactData } = useGetCustomer<ContactDataResponse>(
        GET_CONTACT_CUSTOMER_DATA_QUERY,
        undefined,
        { fetchPolicy: 'network-only' }
    );

    const receiptEmails = (customerContactData?.currentCustomer?.emails || []).filter(e => e.type.id === EMAIL_TYPES.RECEIPT_COPY);
    const mainEmail = (customerContactData?.currentCustomer?.emails || []).find(e => e.type.id === EMAIL_TYPES.MAIN_EMAIL)?.address ?? null;

    // Show pending-invoice warning:
    // - Company: has K-transactions but no unpaid invoices yet this month
    // - Non-company: has K-transactions and today is 1st-15th
    const hasPendingInvoiceWarning =
        hasKTransactionsThisMonth &&
        (isCompany ? unpaidInvoicesCount === 0 : isInPreInvoicePeriod);

    const handleBalanceOptionClick = (option: 'refund' | 'donate') => {
        setRemainingBalanceChoice(option);
    }

    const bankDetailsValid = (): boolean => {
        if (remainingBalance === 0) return true;
        if (remainingBalanceChoice === null) return false;
        if (remainingBalanceChoice === 'donate') return true;
        // refund
        if (!isCompany) {
            return (
                bankClearing.length >= 4 && !bankClearingError &&
                bankAccount.length >= 7 && !bankAccountError
            );
        }
        return bankGiro.replace(/-/g, '').length >= 3 && !bankGiroError;
    };

    const isSubmitDisabled = !confirmChecked || !bankDetailsValid();

    const [addCustomerTask, { loading: mutationLoading, error: mutationError }] =
        useMutation<AddCustomerTaskResponse, AddCustomerTaskInput>(ADD_CUSTOMER_TASK_MUTATION);

    const { freezeCustomer, loading: freezeLoading } = useUpdateCustomerFrozen();

    /**
     * Compute due date for the account deletion task.
     *
     * Rules:
     *  - Uninvoiced K-transactions OR (unpaid invoices + no balance) → 3rd of this month if
     *    today is 1st or 2nd, otherwise 3rd of next month.
     *  - All other cases (only balance, balance+invoices, nothing) → today (null = no due date).
     */
    const computeDueDate = (): string | null => {
        const needsExtendedDate =
            kTransactions.length > 0 ||
            (unpaidInvoicesCount > 0 && remainingBalance === 0);

        if (!needsExtendedDate) return null;

        const today = new Date();
        const target = new Date();

        if (today.getDate() <= 2) {
            // 1st or 2nd of month → 3rd of this month
            target.setDate(3);
        } else {
            // Otherwise → 3rd of next month
            target.setMonth(target.getMonth() + 1);
            target.setDate(3);
        }
        target.setHours(0, 0, 0, 0);
        return target.toISOString();
    };

    /**
     * Build description and fire the addCustomerTask mutation.
     */
    const handleCustomerTask = async () => {
        const customerId = customerBasicData?.currentCustomer?.id;
        const customerNumber = customerBasicData?.currentCustomer?.customerNumber;
        if (!customerId) return;

        const currentDate = new Date().toISOString();
        let description = `Kund har begärt att få sitt konto raderat, ${currentDate}.\r\n`;
        description += `Kundnummer: ${customerNumber}\r\n`;
        description += `Namn: ${customerBasicData?.currentCustomer?.name}\r\n`;

        if (remainingBalance !== 0) {
            description += `Återstående balans smartkort: ${remainingBalance}kr.\r\n`;
            if (remainingBalanceChoice === 'refund') {
                description += `Kund väljer banköverföring av saldo.\r\n`;
                if (!isCompany) {
                    description += `Clearingnummer: ${bankClearing}. Kontonummer: ${bankAccount}.\r\n`;
                } else {
                    description += `Bankgiro/Plusgiro: ${bankGiro}.\r\n`;
                }
            } else if (remainingBalanceChoice === 'donate') {
                description += `Kund donerar saldo till Cancerfonden.\r\n`;
                if (mainEmail) {
                    description += `Kundens e-postadress (för tackmail): ${mainEmail}.\r\n`;
                }
            }
        }

        if (unpaidInvoicesCount > 0) {
            description += `Kund har ${unpaidInvoicesCount} obetald${unpaidInvoicesCount > 1 ? 'a fakturor' : ' faktura'}.\r\n`;
        }

        if (kTransactions.length > 0) {
            description += `Kund har ${kTransactions.length} ej fakturerad${kTransactions.length > 1 ? 'e transaktioner' : ' transaktion'} denna månad.\r\n`;
        }

        const dueDate = computeDueDate();

        try {
            await addCustomerTask({
                variables: {
                    customerId: Number(customerId),
                    description,
                    dueDate,
                },
            });
            // we are not able to freeze account right now
            /*const freezeResult = await freezeCustomer({
                id: Number(customerId),
                reason: 'Frozen by delete account',
            });
            if (freezeResult.error) {
                console.error('Failed to freeze customer account:', freezeResult.error);
            }*/
            setHasSubmitted(true);
        } catch (err) {
            console.error('Failed to create customer task:', err);
        }
    };

    // Animation for customer data loading
    if (customerLoading) {
        return (
            <div className="skeleton-invoice-page">
                <div className="skeleton-page-content-wrapper">
                    <FetchingAnimation width={'100'} maxWidth={true} height={500} marginBottom={10} marginTop={15} />
                </div>
            </div>
        );
    }

    return (
        <div className="Section delete-account-page">
            {!hasSubmitted ? (
                <div className="Row Row--JustifyLeft">
                    <div className="Row__Inner header-intro">
                        <h1>Ta bort konto</h1>
                        <p>Vi är ledsna att se dig gå. Innan du bekräftar uppsägningen, vänligen se över dina aktiva tjänster och eventuella kvarvarande saldon.</p>
                    </div>

                    <div className="Row__Inner content-wrapper">

                        <div className="delete-account-content left-side">
                            {unpaidInvoices.length > 0 && (
                                <div className="unpaid-invoices">
                                    <WarningCircleIcon width={24} height={24} />


                                    <div className="unpaid-invoices-content">
                                        <h5 className="body-text">
                                            {unpaidInvoicesCount > 1 ? `Du har ${unpaidInvoicesCount} obetalda fakturor` : `Du har en obetald faktura`}
                                        </h5>
                                        <p>
                                            För att kunna avsluta kontot helt behöver alla skulder vara betalda.
                                            Efter bekräftat borttagning så kommer ditt konto att spärras men du kommer fortfarande att ha tillgång till transaktioner och fakturor.
                                        </p>

                                        {hasPendingInvoiceWarning && (
                                            isCompany
                                                ? <p>`Du har ${kTransactions.length} transaktion${kTransactions.length > 1 ? 'er' : ''} denna månad som ännu inte fakturerats. En faktura för dessa transaktioner kommer att skapas och behöver betalas.`</p>
                                                : <p>`Du har ${kTransactions.length} transaktion${kTransactions.length > 1 ? 'er' : ''} denna månad som ännu inte fakturerats. Fakturan skapas den 16:e och behöver betalas innan kontot kan avslutas helt.`</p>
                                        )}

                                        <Link href="/mitt-konto/fakturor-kontobesked#invoices">
                                            {unpaidInvoicesCount > 1 ? 'Se obetalda fakturor' : 'Se obetald faktura'}
                                            <ArrowRightIcon width={22} height={22} />
                                        </Link>
                                    </div>
                                </div>
                            )}

                            <div className="white-pale-section shadow-one">
                                <div className="active-services">

                                    <h5 className="body-text">
                                        <ServicesIcon width={28} height={28} />
                                        {(hasActiveSmartCard || hasActiveCreditCard || hasAppAgreement) ? (
                                            'Tjänster som kommer att avslutas'
                                        ) : (
                                            'Spärra konto'
                                        )}
                                    </h5>
                                    <p>
                                        {(hasActiveSmartCard || hasActiveCreditCard || hasAppAgreement) ? (
                                            <>
                                                Nedanstående tjänster kommer inte gå att använda längre när avslutet är bekräftat
                                                {(smartCardCount > 0 || creditCardCount > 0) ? ' och dina kort kommer inte gå att tanka med efter det.' : '.'}
                                            </>
                                        ) : (
                                            <>
                                                Ditt konto kommer att spärras och ditt ärende kommer att hanteras av kundservice så snart som möjligt.
                                            </>
                                        )}
                                    </p>
                                    <ul>
                                        {hasActiveSmartCard &&
                                            <li className="smartcard">
                                                {smartCardCount > 1 ? <CardsStackIcon width={28} height={28} /> : <SingleCardIcon width={28} height={28} />}
                                                Smartkort - {smartCardCount}st
                                            </li>
                                        }

                                        {hasActiveCreditCard &&
                                            <li className={classNames('creditcard', {
                                                'company': isCompany
                                            })}>
                                                {creditCardCount > 1 ? <CardsStackIcon width={28} height={28} /> : <SingleCardIcon width={28} height={28} />}
                                                {isCompany ? 'Företagskort' : 'Tankkort'} - {creditCardCount}st
                                            </li>
                                        }

                                        {hasAppAgreement &&
                                            <li>
                                                <MobileIcon width={28} height={28} />
                                                Inloggning för Qstar-appen
                                            </li>
                                        }

                                        {receiptEmails.length > 0 &&
                                            <li>
                                                <ReceiptIcon width={26} height={26} className="blue" />
                                                Kvittokopior - {receiptEmails.length} {receiptEmails.length > 1 ? 'e-postadresser' : 'e-postadress'}
                                            </li>
                                        }
                                    </ul>
                                </div>
                            </div>

                            {remainingBalance !== 0 &&
                                <div className="white-pale-section shadow-one balance-section">
                                    <div className="header">
                                        <h5 className="body-text">
                                            <WalletIcon width={28} height={28} />
                                            Smartkortsaldo
                                        </h5>
                                        <span>
                                            {formatSEK(remainingBalance)}
                                        </span>
                                    </div>

                                    <p>
                                        Välj nedan hur du vill hantera återstående saldo på ditt smartkort.
                                    </p>


                                    <div className="handle-remaining-balance">
                                        <div className="remaining-balance-options">
                                            <div
                                                role="button"
                                                className={classNames(
                                                    'remaining-balance-option',
                                                    'refund-to-bank',
                                                    {
                                                        'selected': remainingBalanceChoice === 'refund',
                                                        'none-selected': remainingBalanceChoice === null
                                                    }
                                                )}
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        handleBalanceOptionClick('refund');
                                                    }
                                                }}
                                                onClick={() => { handleBalanceOptionClick('refund') }}
                                            >
                                                <span className="svg-icon-wrapper">
                                                    <BankIcon width={28} height={28} />
                                                </span>
                                                <h6 className="body-text">Banköverföring</h6>
                                                {isCompany ? (
                                                    <p>Saldot betalas ut till angivet bank/plusgiro inom 3-5 arbetsdagar.</p>
                                                ) : (
                                                    <p>Saldot betalas ut till angivet bankkonto inom 3-5 arbetsdagar.</p>
                                                )}
                                            </div>

                                            <div
                                                role="button"
                                                className={classNames(
                                                    'remaining-balance-option',
                                                    'donation', {
                                                    'selected': remainingBalanceChoice === 'donate',
                                                    'none-selected': remainingBalanceChoice === null
                                                })}
                                                onClick={() => { setRemainingBalanceChoice('donate') }}
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        handleBalanceOptionClick('donate');
                                                    }
                                                }}
                                            >
                                                <span className="svg-icon-wrapper">
                                                    <DonationIcon width={28} height={28} />
                                                </span>
                                                <h6 className="body-text">Donation till välgörenhet</h6>
                                                <p>Saldot doneras till välgörenhetsorganisationen Cancerfonden.</p>

                                            </div>
                                        </div>

                                        {remainingBalanceChoice === 'refund' && (
                                            <div className="form-bank-details">
                                                <form id="bank-details">
                                                    <p>Fyll i dina {!isCompany ? 'bankuppgifter nedan' : 'bankgiro/plusgiro-uppgifter nedan'}. Efter bekräftat avslut hanterar vi ditt ärende så snart vi kan. Överföringen tar sedan 3-5 arbetsdagar.</p>

                                                    {!isCompany ? (
                                                        <>
                                                            <div className="bankclearing-wrapper">
                                                                <label htmlFor="bankClearing" className="body-text">Clearingnummer</label>
                                                                <input
                                                                    type="text"
                                                                    id="bankClearing"
                                                                    name="bankClearing"
                                                                    placeholder="Clearingnummer"
                                                                    maxLength={6}
                                                                    value={bankClearing}
                                                                    onChange={handleBankClearingChange}
                                                                    onBlur={handleBankClearingBlur}
                                                                />
                                                                {bankClearingError && <span className="input-error">{bankClearingError}</span>}
                                                            </div>

                                                            <div className="bankaccount-wrapper">
                                                                <label htmlFor="bankAccount" className="body-text">Kontonummer</label>
                                                                <input
                                                                    type="text"
                                                                    id="bankAccount"
                                                                    name="bankAccount"
                                                                    placeholder="Kontonummer"
                                                                    maxLength={16}
                                                                    value={bankAccount}
                                                                    onChange={handleBankAccountChange}
                                                                    onBlur={handleBankAccountBlur}
                                                                />
                                                                {bankAccountError && <span className="input-error">{bankAccountError}</span>}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="bankgiro-wrapper">
                                                            <label htmlFor="bankGiro" className="body-text">Bankgiro/plusgiro</label>
                                                            <input
                                                                type="text"
                                                                id="bankGiro"
                                                                name="bankGiro"
                                                                placeholder="Fyll i bankgiro eller plusgiro"
                                                                maxLength={9}
                                                                value={bankGiro}
                                                                onChange={handleBankGiroChange}
                                                                onBlur={handleBankGiroBlur}
                                                            />
                                                            {bankGiroError && <span className="input-error">{bankGiroError}</span>}
                                                        </div>
                                                    )}
                                                </form>
                                            </div>
                                        )}

                                        {remainingBalanceChoice === 'donate' && (
                                            <div className="donation-info">
                                                <p>Tack för att du väljer att donera ditt återstående saldo till Cancerfonden! Din generositet hjälper till att finansiera livsviktig cancerforskning och stödja drabbade individer och deras familjer.</p>
                                                <p>Efter att du bekräftat borttagningen av ditt konto kommer vi att hantera din donation så snart som möjligt.</p>
                                            </div>
                                        )}
                                    </div>

                                </div>
                            }

                        </div>

                        <div className="delete-account-confirmation right-side">
                            <div className="blue-section shadow-one has-dark-bg">
                                <h5 className="body-text">
                                    Bekräfta borttagning
                                </h5>
                                <p>
                                    När du klickar på knappen nedan så kommer ett ärende om borttagning att skapas och du kommer inte längre att kunna använda några av våra tjänster när ärendet är bearbetat.
                                    {hasActiveSmartCard && ' Innan du kan avsluta behöver du välja hur du vill hantera ditt återstående saldo på smartkortet.'}
                                </p>
                                <div className="confirmation-checkbox">
                                    <input
                                        type="checkbox"
                                        id="confirm-delete"
                                        name="confirm-delete"
                                        checked={confirmChecked}
                                        onChange={e => setConfirmChecked(e.target.checked)}
                                        onKeyDown={
                                            (e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setConfirmChecked(prev => !prev);
                                                }
                                            }
                                        }
                                    />
                                    <label htmlFor="confirm-delete" className="body-text confirm-checkbox">
                                        Jag förstår att radering av kontot är permanent.
                                    </label>
                                </div>
                                <RightsWrapper
                                    disableButtonClasses='body-text'
                                    onlySuperUser={true}
                                >
                                    <button
                                        className="dashboard-button no-margin Button"
                                        disabled={isSubmitDisabled || mutationLoading || freezeLoading}
                                        onClick={handleCustomerTask}
                                    >
                                        {(mutationLoading || freezeLoading) ? 'Behandlar...' : 'Ta bort mitt konto'}
                                    </button>
                                </RightsWrapper>

                            </div>

                            <div className="other-info white-pale-section shadow-one">
                                <div className="contact-support">
                                    <span className="svg-icon-wrapper round orange">
                                        <SupportIcon width={24} height={24} />
                                    </span>
                                    <div>
                                        <h5 className="body-text">Behöver du hjälp?</h5>
                                        <p>Prata med kundservice</p>
                                    </div>


                                </div>
                                <Link className="Button dashboard-button no-margin" href="/kontakt">
                                    Kontakta oss
                                </Link>
                            </div>
                        </div>

                    </div>
                </div>
            ) : (
                <div className="Row Row--JustifyLeft">
                    <div className="Row__Inner delete-confirmation-wrapper white-pale-section shadow-one">
                        <h1>Bekräftelse</h1>
                        <p>Ett ärende om borttagning har skapats och kommer att behandlas så snart som möjligt. Ditt konto kommer även spärras efter behandling av ärendet i och med detta går det inte använda våra tjänster längre.</p>
                        <div className="button-wrapper">
                            <Link href="/" className="dashboard-button no-margin">
                                Tillbaka till översikten
                            </Link>

                            <Link href="/loggaut/" className="dashboard-button no-margin">
                                <LogoutIcon width={20} height={20} />
                                Logga ut
                            </Link>
                        </div>

                    </div>
                </div>
            )}

        </div>
    )
}

export default DeleteAccountPage
